import React, { useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, Row, Col, Space, Tabs } from 'antd';
import styled from 'styled-components';
import ExportButton from '../../../components/common/ExportButton';
import DashboardExportPanel from '../../../components/common/DashboardExportPanel';

// charts...
import { EX1NumberOfCyclesAndProcessesHorizontalStackedBarChart } from '@/components/dashboard/charts/EX1_NumberOfCyclesAndProcesses_HorizontalStackedBarChart';
import { EX1ControlDistributionByCycleDonutChart } from '@/components/dashboard/charts/EX1_ControlDistributionByCycle_DonutChart';
import { EX1ControlDistributionByProcessGroupedHorizontalBarChart } from '@/components/dashboard/charts/EX1_ControlDistributionByProcess_GroupedHorizontalBarChart';
import { EX1ControlDistributionKeyVsNonKeyByCycleGroupedHorizontalBarChart } from '@/components/dashboard/charts/EX1_ControlDistributionKeyVsNonKeyByCycle_GroupedHorizontalBarChart';
import { EX1RiskRankingOfProcessesByCycleGroupedHorizontalBarChart } from '@/components/dashboard/charts/EX1_RiskRankingOfProcessesByCycle_GroupedHorizontalBarChart';
import { EX1RiskRankingOfControlsByProcessGroupedHorizontalBarChart } from '@/components/dashboard/charts/EX1_RiskRankingOfControlsByProcess_GroupedHorizontalBarChart';
import { EX1ObjectivesDistributionByCyclePieChart } from '@/components/dashboard/charts/EX1_ObjectivesDistributionByCycle_PieChart';
import { EX1SpecificIdentifiedFraudRisksByCyclePieChart } from '@/components/dashboard/charts/EX1_SpecificIdentifiedFraudRisksByCycle_PieChart';
import { EX1TotalControlEffectivenessDonutChart } from '@/components/dashboard/charts/EX1_TotalControlEffectiveness_DonutChart';
import { EX1OpenGapRankingByCycleAndRiskLevelHorizontalGroupedBarChart } from '@/components/dashboard/charts/EX1_OpenGapRankingByCycleAndRiskLevel_HorizontalGroupedBarChart';
import { EX1NewGapsVsArchivedFiscalYearWaterfallChart } from '@/components/dashboard/charts/EX1_NewGapsVsArchivedFiscalYear_WaterfallChart';
import { EX1ProcessOwnercertificationCompletionStatusGrid } from '@/components/dashboard/charts/EX1_ProcessOwnercertificationCompletionStatus_Grid';
import { EX1ProcessOwnersByProcessGrid } from '@/components/dashboard/charts/EX1_ProcessOwnersByProcess_Grid';
import { EX1WalkthroughDesignCompletionStatusProgressBar } from '@/components/dashboard/charts/EX1_WalkthroughDesignCompletionStatus_ProgressBar';
import { EX1InterimTestingCompletionStatusByCycleStackedChart } from '@/components/dashboard/charts/EX1_InterimTestingCompletionStatusByCycle_StackedChart';
import { EX1FinalTestingCompletionStatusByCycleStackedChart } from '@/components/dashboard/charts/EX1_FinalTestingCompletionStatusByCycle_StackedChart';
import { EX1WorkWeeksRemainingWidget } from '@/components/dashboard/charts/EX1_WorkWeeksRemaining_Widget';
import { EX1AuditorCoverageForRemainingWorkStackedBar } from '@/components/dashboard/charts/EX1_AuditorCoverageForRemainingWork_StackedBar';
import { EX1FinancialApplicationsScopeGrid } from '@/components/dashboard/charts/EX1_FinancialApplicationsScope_Grid';
import { EX1NotesToManagementWidget } from '@/components/dashboard/charts/EX1_NotesToManagement_Widget';
import { EX1CycleTestingStatusGrid } from '@/components/dashboard/charts/EX1_CyclesTestingStatus_Grid';
import { EX1AuditorAssignmentDashboardGrid } from '@/components/dashboard/charts/EX1_AuditorAssignmentDashboard_Grid';
import { EX1QuarterlyYTDGapActivityWaterfallChart } from '@/components/dashboard/charts/EX1_QuarterlyYTDGapActivity_WaterfallChart';

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1f2937 !important;
`;

// Reuse the same tab styling from FinancialStatementsPage
const TabsContainer = styled.div`
  position: relative;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    .ant-tabs-tab-btn {
      color: #6b7280;
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563eb;
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563eb;
      }
    }
  }
`;

const ExportStage = styled.div`
  position: fixed;
  left: -10000px;
  top: 0;
  width: 1100px;
  background: #fff;
  padding: 16px;
  z-index: -1;

  /* make sure nothing inside is display:none due to tabs */
  .ant-tabs-tabpane,
  .ant-tabs-tabpane-hidden {
    display: block !important;
    visibility: visible !important;
    height: auto !important;
    overflow: visible !important;
  }

  /* Only affect Metric 2 donut export tooltip panel */
  .metric2-donut .export-only {
    display: block !important;
  }

  /* Metric 2: expand the container height in export so the panel is not clipped */
  .metric2-donut[data-export-height] {
    height: attr(data-export-height px) !important;
  }
`;

const Section = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginTop: 16 }}>{children}</div>
);

const ChartRow = ({ children }: { children: React.ReactNode }) => (
  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
    <Col xs={24}>{children}</Col>
  </Row>
);

// Valid tab keys for URL sync (e.g. dashboard?tab=testing)
const TAB_KEYS = ['hierarchy', 'risk-assessment', 'testing', 'gap', 'certification-status', 'admin'] as const;
type TabKey = (typeof TAB_KEYS)[number];

function ExportBlock({
  exportKey,
  children,
}: {
  exportKey: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-export-key={exportKey}
      style={{ background: '#fff', padding: 16, borderRadius: 8 }}
    >
      {children}
    </div>
  );
}

const DashboardReportingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Export panel state
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);

  // Tab from URL (e.g. dashboard?tab=testing)
  const activeTab = useMemo(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && TAB_KEYS.includes(tabParam as TabKey)) {
      return tabParam;
    }
    return 'hierarchy';
  }, [searchParams]);

  const handleTabChange = useCallback(
    (key: string) => {
      setSearchParams({ tab: key }, { replace: true });
    },
    [setSearchParams]
  );

  const hierarchyContent = (
    <Section>
      <ChartRow>
        <ExportBlock exportKey='numberOfCyclesAndProcesses'>
          <EX1NumberOfCyclesAndProcessesHorizontalStackedBarChart segmented />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='controlDistributionByCycle'>
          <EX1ControlDistributionByCycleDonutChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='controlDistributionByProcess'>
          <EX1ControlDistributionByProcessGroupedHorizontalBarChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='controlDistributionKeyVsNonKeyByCycle'>
          <EX1ControlDistributionKeyVsNonKeyByCycleGroupedHorizontalBarChart />
        </ExportBlock>
      </ChartRow>
    </Section>
  );

  const riskAssessmentContent = (
    <Section>
      <ChartRow>
        <ExportBlock exportKey='riskRankingOfProcessesByCycle'>
          <EX1RiskRankingOfProcessesByCycleGroupedHorizontalBarChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='riskRankingOfControlsByProcess'>
          <EX1RiskRankingOfControlsByProcessGroupedHorizontalBarChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='objectivesDistributionByCycle'>
          <EX1ObjectivesDistributionByCyclePieChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='specificIdentifiedFraudRisksByCycle'>
          <EX1SpecificIdentifiedFraudRisksByCyclePieChart />
        </ExportBlock>
      </ChartRow>
    </Section>
  );

  const testingContent = (
    <Section>
      <ChartRow>
        <ExportBlock exportKey='cycleTestingStatus'>
          <EX1CycleTestingStatusGrid />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='totalEffectiveVsIneffectiveVsUntestedControls'>
          <EX1TotalControlEffectivenessDonutChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='walkthroughDesignCompletionStatus'>
          <EX1WalkthroughDesignCompletionStatusProgressBar />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='interimTestingCompletionStatus'>
          <EX1InterimTestingCompletionStatusByCycleStackedChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='finalTestingCompletionStatus'>
          <EX1FinalTestingCompletionStatusByCycleStackedChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='workWeeksRemainingInAuditPeriod'>
          <EX1WorkWeeksRemainingWidget />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='auditorCoverageForRemainingWork'>
          <EX1AuditorCoverageForRemainingWorkStackedBar />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='auditorAssignmentByMonth'>
          <EX1AuditorAssignmentDashboardGrid />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='financialApplicationsScope'>
          <EX1FinancialApplicationsScopeGrid />
        </ExportBlock>
      </ChartRow>
    </Section>
  );

  const gapContent = (
    <Section>
      <ChartRow>
        <ExportBlock exportKey='openGAPRankingByCycleAndRemediationTimeline'>
          <EX1OpenGapRankingByCycleAndRiskLevelHorizontalGroupedBarChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='newGapsVsArchivedFiscalYearWaterfall'>
          <EX1NewGapsVsArchivedFiscalYearWaterfallChart />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='quarterlyYTDGapActivity'>
          <EX1QuarterlyYTDGapActivityWaterfallChart />
        </ExportBlock>
      </ChartRow>
    </Section>
  );

  const certificationStatusContent = (
    <Section>
      <ChartRow>
        <ExportBlock exportKey='listingOfProcessOwnersByProcess'>
          <EX1ProcessOwnersByProcessGrid />
        </ExportBlock>
      </ChartRow>
      <ChartRow>
        <ExportBlock exportKey='processOwnerCertificationCompletionStatus'>
          <EX1ProcessOwnercertificationCompletionStatusGrid />
        </ExportBlock>
      </ChartRow>
      {/* If you have 3 more "certification" KPIs, add them here */}
    </Section>
  );

  const adminContent = (
    <Section>
      <ChartRow>
        <EX1NotesToManagementWidget />
      </ChartRow>
    </Section>
  );

  const tabItems = useMemo(
    () => [
      {
        key: 'hierarchy',
        label: 'Hierarchy',
        children: hierarchyContent,
      },
      {
        key: 'risk-assessment',
        label: 'Risk Assessment',
        children: riskAssessmentContent,
      },
      {
        key: 'testing',
        label: 'Testing',
        children: testingContent,
      },
      {
        key: 'gap',
        label: 'GAPS',
        children: gapContent,
      },
      {
        key: 'certification-status',
        label: 'Certification Status',
        children: certificationStatusContent,
      },
      {
        key: 'admin',
        label: 'Admin',
        children: adminContent,
      },
    ],
    [],
  );

  // Handle Export button click
  const handleOpenExportPanel = () => {
    setIsExportPanelOpen(true);
  };

  const handleCloseExportPanel = () => {
    setIsExportPanelOpen(false);
  };

  const exportData = useMemo(() => {
    return [];
  }, []);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>Dashboard & Reporting</PageTitle>
        <Space>
          {/* <Button icon={<FilterOutlined />}>Filters</Button>
          <Button type='primary' icon={<DownloadOutlined />}>
            Export
          </Button> */}
          <ExportButton onClick={handleOpenExportPanel} title='Export' />
        </Space>
      </PageHeader>

      <TabsContainer>
        <StyledTabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          // Optional behavior knobs:
          // destroyInactiveTabPane={false} // keep charts mounted (faster tab switching)
          // animated={{ inkBar: true, tabPane: false }} // often nicer for heavy charts
        />
      </TabsContainer>

      {/* Export Panel */}
      <DashboardExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        charts={[
          {
            key: 'numberOfCyclesAndProcesses',
            title: 'Number of Cycles and Processes',
          },
          {
            key: 'controlDistributionByCycle',
            title: 'Control Distribution by Cycle',
          },
          {
            key: 'controlDistributionByProcess',
            title: 'Control Distribution by Process',
          },
          {
            key: 'controlDistributionKeyVsNonKeyByCycle',
            title: 'Control Distribution Key vs Non-Key by Cycle',
          },
          {
            key: 'riskRankingOfProcessesByCycle',
            title: 'Risk Ranking of Processes by Cycle',
          },
          {
            key: 'riskRankingOfControlsByProcess',
            title: 'Risk Ranking of Controls by Process',
          },
          {
            key: 'objectivesDistributionByCycle',
            title: 'Objectives Distribution by Cycle',
          },
          {
            key: 'specificIdentifiedFraudRisksByCycle',
            title: 'Specific Identified Fraud Risks by Cycle',
          },
          {
            key: 'cycleTestingStatus',
            title: 'Cycle Testing Status Breakdown',
          },
          {
            key: 'totalEffectiveVsIneffectiveVsUntestedControls',
            title: 'Total Effective vs Ineffective vs Untested Controls',
          },
          {
            key: 'walkthroughDesignCompletionStatus',
            title: 'Walkthrough/Design completion status',
          },
          {
            key: 'interimTestingCompletionStatus',
            title: 'Interim Testing completion status',
          },
          {
            key: 'finalTestingCompletionStatus',
            title: 'Final Testing completion status',
          },
          {
            key: 'workWeeksRemainingInAuditPeriod',
            title: 'Work weeks remaining in audit period',
          },
          {
            key: 'auditorCoverageForRemainingWork',
            title: 'Auditor Coverage for Remaining Work',
          },
          {
            key: 'auditorAssignmentByMonth',
            title: 'Auditor Assignment by Month',
          },
          {
            key: 'financialApplicationsScope',
            title: 'Financial Applications',
          },
          {
            key: 'openGAPRankingByCycleAndRemediationTimeline',
            title: 'Open GAP Ranking by Cycle & Remediation Timeline',
          },
          {
            key: 'newGapsVsArchivedFiscalYearWaterfall',
            title: 'New Gaps vs Archived (Fiscal Year Waterfall)',
          },
          {
            key: 'quarterlyYTDGapActivity',
            title: 'Quarterly/YTD GAP Activity',
          },
          {
            key: 'listingOfProcessOwnersByProcess',
            title: 'Listing of Process Owners by Process',
          },
          {
            key: 'processOwnerCertificationCompletionStatus',
            title: 'Process Owner Certification Completion Status',
          },
        ]}
        fileName='Dashboard_Report_'
        title='Dashboard Report'
      />

      <ExportStage id='dashboard-export-stage'>
        {hierarchyContent}
        {riskAssessmentContent}
        {testingContent}
        {gapContent}
        {certificationStatusContent}
      </ExportStage>
    </PageContainer>
  );
};

export default DashboardReportingPage;
